<template>
  <button
    class="rt-video-thumb"
    :aria-label="`Play video: ${title}`"
    @click="$emit('openVideo')"
  >
    <span class="sr-only">{{ title }}</span>

    <!-- Play overlay -->
    <div class="play-overlay">
      <!-- Clean SVG play icon -->
      <svg
        width="72"
        height="72"
        viewBox="0 0 100 100"
        aria-hidden="true"
      >
        <circle
          cx="50"
          cy="50"
          r="46"
          fill="rgba(0,0,0,0.45)"
        />
        <polygon
          points="40,30 75,50 40,70"
          fill="currentColor"
        />
      </svg>
    </div>

    <!-- Thumbnail -->
    <div
      v-if="resolvedThumbUrl"
      class="thumbnail-image"
      :style="{ backgroundImage: `url('${resolvedThumbUrl}')` }"
      role="img"
      :aria-label="title"
    />
    <div
      v-else
      class="thumbnail-placeholder"
    >
      <span>Video Thumbnail</span>
      <span>{{ videoId || 'No ID' }}</span>
    </div>
  </button>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  videoId: { type: String, required: true },
  title: { type: String, default: 'Video' },
  thumbUrl: { type: String, default: '' },
})

defineEmits(['openVideo'])

const resolvedThumbUrl = computed(() => {
  // Use YouTube thumbnail instead of album cover
  if (!props.videoId) {
    return ''
  }
  // Try different thumbnail resolutions as fallback
  const thumbUrl = `https://img.youtube.com/vi/${props.videoId}/hqdefault.jpg`
  return thumbUrl
})
</script>

<style scoped>
.rt-video-thumb {
  display: block;
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.3);
  transition: background-color 0.2s ease;
  z-index: 2;
}

.rt-video-thumb:hover .play-overlay {
  background: rgba(0, 0, 0, 0.4);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.thumbnail-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: var(--card-bg-color);
  color: var(--text-color);
  font-size: 0.9rem;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
